import React, { useState, useMemo, useEffect } from "react";
import "./occupancy.less";
import { Reit, YearData, GrossAssetOccupancy } from "../../../../types/reit.types";
import { safeFormat } from "../../../../utils/financialHelper";

interface OccupancyProps {
  reit: Reit | null;
}

interface OccupancyRow {
  metric: string;
  values: (string | number)[];
}

const Occupancy: React.FC<OccupancyProps> = ({ reit }) => {
  const [view, setView] = useState<"Yearly" | "Quarterly">("Yearly");
  const [year, setYear] = useState<string>("");

  // derive years for dropdown
  const years = useMemo(() => {
    if (!reit?.years) return [];
    return reit.years.map((y: YearData) => `FY ${y.year}`);
  }, [reit]);

  // set default year
  useEffect(() => {
    if (years.length && !year) {
      setYear(years[0]);
    }
  }, [years, year]);

  // build occupancy table data
  const occupancyData: OccupancyRow[] = useMemo(() => {
    if (!reit?.years?.length || !year) return [];

    const yearValue = year.replace("FY ", "");
    const yearData = reit.years.find((y) => y.year === yearValue);
    if (!yearData) return [];

    if (view === "Yearly") {
      const occ: GrossAssetOccupancy = yearData.grossAssetOccupancy;
      return Object.keys(occ).map((key) => ({
        metric: occ[key as keyof GrossAssetOccupancy]?.key ?? key,
        values: [safeFormat(occ[key as keyof GrossAssetOccupancy]?.value)],
      }));
    } else {
      const quarters = (yearData as any).quarters || [];
      if (!quarters.length) return [];

      const keys = Object.keys(
        (quarters[0]?.grossAssetOccupancy as GrossAssetOccupancy) || {}
      );

      return keys.map((key) => ({
        metric:
          quarters[0]?.grossAssetOccupancy?.[key as keyof GrossAssetOccupancy]
            ?.key ?? key,
        values: quarters.map((q: { grossAssetOccupancy: GrossAssetOccupancy }) =>
          safeFormat(q.grossAssetOccupancy?.[key as keyof GrossAssetOccupancy]?.value)
        ),
      }));
    }
  }, [reit, year, view]);

  return (
    <section className="occupancy">
      <div className="occupancy__card">
        {/* Header */}
        <div className="occupancy__header">
          <h2>Occupancy</h2>

          <div className="occupancy__subheader">
            {/* Toggle */}
            <div className="occupancy__tabs">
              <button
                className={view === "Yearly" ? "active" : ""}
                onClick={() => setView("Yearly")}
              >
                Yearly
              </button>
              <button
                className={view === "Quarterly" ? "active" : ""}
                onClick={() => setView("Quarterly")}
              >
                Quarterly
              </button>
            </div>

            {/* Dropdown */}
            <div className="occupancy__dropdown">
              <div className="custom-select">
                <select value={year} onChange={(e) => setYear(e.target.value)}>
                  {years.map((y, idx) => (
                    <option key={idx} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="occupancy__table">
          <table>
            <thead>
              <tr>
                <th>Metric</th>
                {view === "Quarterly" ? (
                  <>
                    <th>Q1</th>
                    <th>Q2</th>
                    <th>Q3</th>
                    <th>Q4</th>
                  </>
                ) : (
                  <th>Value</th>
                )}
              </tr>
            </thead>
            <tbody>
              {occupancyData.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.metric}</td>
                  {row.values.map((val, i) => (
                    <td key={i}>{val}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default Occupancy;
